/**
 * Cross-tab auth synchronization without broadcasting raw tokens.
 */

export type AuthSyncEventType =
  | 'auth:token-updated'
  | 'auth:logout'
  | 'auth:session-expired';

const CHANNEL_NAME = 'march-auth-sync';

function postMessage(type: AuthSyncEventType): void {
  if (typeof window === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage({ type, ts: Date.now() });
    channel.close();
  } catch {
    // BroadcastChannel unavailable — storage events may still apply for persistent sessions.
  }
}

export function broadcastAuthTokenUpdated(): void {
  postMessage('auth:token-updated');
}

export function broadcastAuthLogout(): void {
  postMessage('auth:logout');
}

export function broadcastAuthSessionExpired(): void {
  postMessage('auth:session-expired');
}

export function subscribeAuthSyncEvents(
  handler: (type: AuthSyncEventType) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<{ type?: AuthSyncEventType }>) => {
      const type = event.data?.type;
      if (type) handler(type);
    };
  } catch {
    // Ignore — storage listener still handles persistent sessions.
  }

  const onStorage = (event: StorageEvent) => {
    if (!event.key) return;
    if (event.key.startsWith('march_dev_keycloak_') || event.key === 'march_auth_storage_scope') {
      handler('auth:token-updated');
    }
    if (event.key === 'march_remembered_username') {
      handler('auth:token-updated');
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) channel.close();
    window.removeEventListener('storage', onStorage);
  };
}
